import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import {
  SignalCheckbox, SignalFormDirective,
  SignalNumberField,
  SignalTextField,
} from '@vallift/ngl';
import type { FormField } from '../../../../models/built-form';

interface SliderConfigModel {
  label: string;
  helpText: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  required: boolean;
}

@Component({
  selector: 'app-slider-config-view',
  standalone: true,
  templateUrl: './slider-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalNumberField,
    SignalCheckbox,
    SignalFormDirective,
  ],
})
export class SliderConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<SliderConfigModel>({
    label: '',
    min: 0,
    max: 100,
    step: 1,
    helpText: '',
    defaultValue: 50,
    required: false,
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        min: next.sliderMin ?? 0,
        max: next.sliderMax ?? 100,
        step: next.sliderStep ?? 1,
        defaultValue:
          typeof next.config?.defaultValue === 'number'
            ? next.config.defaultValue
            : 50,
        required: next.required ?? validation.required ?? false,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim() || field.label,
        helpText: draft.helpText.trim() || undefined,
        sliderMin: draft.min,
        sliderMax: draft.max,
        sliderStep: draft.step,
        config: {
          ...field.config,
          defaultValue: draft.defaultValue,
        },
        required: draft.required,
      });
    });
  }
}
